import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import type { NavigationProp } from '@react-navigation/native';
import { syncCatalogSnapshotFromRemote } from '../../../catalog/infra-api'
import { wasCatalogHydratedRecently } from '../../../catalog/infra-api';
import { openSalesList } from '../../../sales/public-api'
import { salesListParamsFromReportsScope } from '../../../sales/public-api';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';
import { reportsQueryScopeHasFilters, ventasProductScopeToQueryScope } from '../../domain/reportsQueryScope'
import type { ReportsVentasProductScope } from '../../domain/reportsTypes'
import type { ReportsQueryScope } from '../../domain/reportsQueryScope';
import { listActiveSuppliersLocal, listCategories, listDistinctProductBrandsLocal } from '../../application/reportsScreenData';
import type { ReportsCustomRange } from '../reportsScreen.utils';
import type { DateRangePreset } from '../reportsScreen.constants';
import { useReportsSalesByProduct } from './useReportsSalesByProduct';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { categoryAppliesToDraftItemType, normalizeProductCategoryId } from '../../../catalog/domain-api'
import type { CategoryRecord } from '../../../catalog/domain-api'
import type { CatalogItemTypeFilter } from '../../../catalog/domain-api';
import { buildCategoryFilterOptions } from '../../../catalog/domain-api'
import type { CategoryFilterOption } from '../../../catalog/domain-api';

type Nav = NavigationProp<Record<string, object | undefined>>;

/** Informes RPC solo distingue físico/servicio; combo/receta se tratan como físico. */
function reportsItemTypeFromCatalogFilter(
  filter: CatalogItemTypeFilter,
): 'all' | 'physical' | 'service' {
  if (filter === 'all') return 'all';
  if (filter === 'service') return 'service';
  return 'physical';
}

function categoryItemTypeFromCatalogFilter(
  filter: CatalogItemTypeFilter,
): 'physical' | 'service' | undefined {
  if (filter === 'all') return undefined;
  if (filter === 'service') return 'service';
  return 'physical';
}

type Params = {
  navigation: Nav;
  currentBranchId: string | null | undefined;
  isVentasSection: boolean;
  isFocused: boolean;
  reportsBootstrapReady: boolean;
  dateRangePreset: DateRangePreset;
  customRangeFrom: Date;
  customRangeTo: Date;
  customRangeParam: ReportsCustomRange | null;
};

export function useReportsVentasListPane({
  navigation,
  currentBranchId,
  isVentasSection,
  isFocused,
  reportsBootstrapReady,
  dateRangePreset,
  customRangeFrom,
  customRangeTo,
  customRangeParam,
}: Params) {
  const [salesByProductRefreshKey, setSalesByProductRefreshKey] = useState(0);
  const [reportCategories, setReportCategories] = useState<CategoryRecord[]>([]);
  const [reportsVentasBrandOptions, setReportsVentasBrandOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [reportsVentasSupplierOptions, setReportsVentasSupplierOptions] = useState<Array<{ id: string; name: string }>>(
    []
  );

  type FiltersState = {
    salesByProductCategory: string | null;
    salesByProductItemTypeFilter: CatalogItemTypeFilter;
    reportsVentasPinnedProductId: string | null;
    reportsVentasPinnedProductLabel: string | null;
    reportsVentasBrandFilter: string | null;
    reportsVentasSupplierFilter: string | null;
    reportsCustomerFilterId: string | null;
    reportsCustomerFilterLabel: string | null;
    reportsOperatorFilterId: string | null;
    reportsOperatorFilterLabel: string | null;
    reportsCashSessionFilterId: string | null;
    reportsHasDiscountFilter: 'all' | 'yes' | 'no';
    reportsPromotionFilterId: string | null;
    reportsPromoCodeFilter: string | null;
  };

  const initialFilters: FiltersState = {
    salesByProductCategory: null,
    salesByProductItemTypeFilter: 'all',
    reportsVentasPinnedProductId: null,
    reportsVentasPinnedProductLabel: null,
    reportsVentasBrandFilter: null,
    reportsVentasSupplierFilter: null,
    reportsCustomerFilterId: null,
    reportsCustomerFilterLabel: null,
    reportsOperatorFilterId: null,
    reportsOperatorFilterLabel: null,
    reportsCashSessionFilterId: null,
    reportsHasDiscountFilter: 'all',
    reportsPromotionFilterId: null,
    reportsPromoCodeFilter: null,
  };

  type FiltersAction =
    | { type: 'SET'; key: keyof FiltersState; value: string | null }
    | { type: 'SET_CUSTOMER'; id: string; label: string }
    | { type: 'CLEAR_CUSTOMER' }
    | { type: 'SET_OPERATOR'; id: string; label: string }
    | { type: 'CLEAR_OPERATOR' }
    | { type: 'PIN_PRODUCT'; productId: string; label: string }
    | { type: 'CLEAR_PIN' }
    | { type: 'RESET' };

  function filtersReducer(state: FiltersState, action: FiltersAction): FiltersState {
    switch (action.type) {
      case 'SET':
        return { ...state, [action.key]: action.value };
      case 'SET_CUSTOMER':
        return { ...state, reportsCustomerFilterId: action.id, reportsCustomerFilterLabel: action.label };
      case 'CLEAR_CUSTOMER':
        return { ...state, reportsCustomerFilterId: null, reportsCustomerFilterLabel: null };
      case 'SET_OPERATOR':
        return { ...state, reportsOperatorFilterId: action.id, reportsOperatorFilterLabel: action.label };
      case 'CLEAR_OPERATOR':
        return { ...state, reportsOperatorFilterId: null, reportsOperatorFilterLabel: null };
      case 'PIN_PRODUCT':
        return { ...state, reportsVentasPinnedProductId: action.productId, reportsVentasPinnedProductLabel: action.label };
      case 'CLEAR_PIN':
        return { ...state, reportsVentasPinnedProductId: null, reportsVentasPinnedProductLabel: null };
      case 'RESET':
        return initialFilters;
      default:
        return state;
    }
  }

  const [filters, dispatchFilters] = useReducer(filtersReducer, initialFilters);

  const {
    salesByProductCategory,
    salesByProductItemTypeFilter,
    reportsVentasPinnedProductId,
    reportsVentasPinnedProductLabel,
    reportsVentasBrandFilter,
    reportsVentasSupplierFilter,
    reportsCustomerFilterId,
    reportsCustomerFilterLabel,
    reportsOperatorFilterId,
    reportsOperatorFilterLabel,
    reportsCashSessionFilterId,
    reportsHasDiscountFilter,
    reportsPromotionFilterId,
    reportsPromoCodeFilter,
  } = filters;

  const bumpSalesByProductRefreshKey = useCallback(() => {
    setSalesByProductRefreshKey((k) => k + 1);
  }, []);

  const salesByProductCategoryOptions = useMemo<CategoryFilterOption[]>(() => {
    const forItemType = categoryItemTypeFromCatalogFilter(salesByProductItemTypeFilter);
    return buildCategoryFilterOptions(reportCategories, {
      allLabel: 'Todas',
      fallbackCategories: [],
      forItemType,
    });
  }, [reportCategories, salesByProductItemTypeFilter]);

  const handleSalesByProductItemTypeFilterChange = useCallback(
    (nextFilter: CatalogItemTypeFilter) => {
      dispatchFilters({ type: 'SET', key: 'salesByProductItemTypeFilter', value: nextFilter });
      if (!salesByProductCategory || nextFilter === 'all') {
        return;
      }
      const normalizedCategoryId = normalizeProductCategoryId(salesByProductCategory);
      const categoryRecord = reportCategories.find(
        (category) => normalizeProductCategoryId(category.slug) === normalizedCategoryId
      );
      const categoryItemType = categoryItemTypeFromCatalogFilter(nextFilter);
      if (
        categoryItemType &&
        categoryRecord &&
        !categoryAppliesToDraftItemType(categoryRecord.appliesTo, categoryItemType)
      ) {
        dispatchFilters({ type: 'SET', key: 'salesByProductCategory', value: null });
      }
    },
    [reportCategories, salesByProductCategory]
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const local = await listCategories();
        if (!cancelled) setReportCategories(local);
      } catch (error) {
        if (!isDatabaseUnavailableError(error)) {
          console.warn('[Reports] No se pudieron leer categorías locales', error);
        }
      }
      try {
        if (!wasCatalogHydratedRecently()) {
          await syncCatalogSnapshotFromRemote();
        }
        const local = await listCategories();
        if (!cancelled && local.length > 0) setReportCategories(local);
      } catch (error) {
        if (!isDatabaseUnavailableError(error)) {
          console.warn('[Reports] No se pudieron sincronizar categorías', error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unsubscribeCatalogSynced = subscribeCatalogSynced(async () => {
      try {
        const local = await listCategories();
        setReportCategories(local);
      } catch (error) {
        console.warn('[Reports] Error al recargar categorías después de sync:', error);
      }
    });

    return () => {
      unsubscribeCatalogSynced();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([listDistinctProductBrandsLocal(), listActiveSuppliersLocal()]).then(([brands, suppliers]) => {
      if (!cancelled) {
        setReportsVentasBrandOptions(brands);
        setReportsVentasSupplierOptions(suppliers);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!salesByProductCategory) {
      return;
    }
    const normalizedCategoryId = normalizeProductCategoryId(salesByProductCategory);
    const isAvailable = salesByProductCategoryOptions.some(
      (option) => option.id !== 'all' && normalizeProductCategoryId(option.id) === normalizedCategoryId
    );
    if (!isAvailable) {
      dispatchFilters({ type: 'SET', key: 'salesByProductCategory', value: null });
    }
  }, [salesByProductCategory, salesByProductCategoryOptions]);

  const ventasProductScopeForRpc = useMemo((): ReportsVentasProductScope => {
    return {
      categoryId: salesByProductCategory,
      itemType: reportsItemTypeFromCatalogFilter(salesByProductItemTypeFilter),
      productId: reportsVentasPinnedProductId,
      search: null,
      brandId: reportsVentasBrandFilter,
      supplierId: reportsVentasSupplierFilter,
    };
  }, [
    salesByProductCategory,
    salesByProductItemTypeFilter,
    reportsVentasPinnedProductId,
    reportsVentasBrandFilter,
    reportsVentasSupplierFilter,
  ]);

  const ventasProductFiltersActiveForUi = useMemo(() => {
    return (
      salesByProductCategory != null ||
      salesByProductItemTypeFilter !== 'all' ||
      reportsVentasPinnedProductId != null ||
      reportsVentasBrandFilter != null ||
      reportsVentasSupplierFilter != null
    );
  }, [
    salesByProductCategory,
    salesByProductItemTypeFilter,
    reportsVentasPinnedProductId,
    reportsVentasBrandFilter,
    reportsVentasSupplierFilter,
  ]);

  const reportsQueryScope = useMemo(
    (): ReportsQueryScope =>
      ventasProductScopeToQueryScope(
        ventasProductScopeForRpc,
        reportsCustomerFilterId,
        reportsOperatorFilterId,
        {
          cashSessionId: reportsCashSessionFilterId,
          hasDiscount: reportsHasDiscountFilter,
          promotionId: reportsPromotionFilterId,
          promoCode: reportsPromoCodeFilter,
        }
      ),
    [
      ventasProductScopeForRpc,
      reportsCustomerFilterId,
      reportsOperatorFilterId,
      reportsCashSessionFilterId,
      reportsHasDiscountFilter,
      reportsPromotionFilterId,
      reportsPromoCodeFilter,
    ]
  );

  const reportsQueryFiltersActive = useMemo(
    () => ventasProductFiltersActiveForUi || reportsQueryScopeHasFilters(reportsQueryScope),
    [ventasProductFiltersActiveForUi, reportsQueryScope]
  );

  const salesByProduct = useReportsSalesByProduct({
    currentBranchId,
    isVentasSection,
    isFocused,
    reportsBootstrapReady,
    dateRangePreset,
    customRangeFrom,
    customRangeTo,
    reportsQueryScope,
    salesByProductRefreshKey,
    bumpSalesByProductRefreshKey,
  });

  const resetVentasFilters = useCallback(() => {
    dispatchFilters({ type: 'RESET' });
  }, []);

  const openBranchSalesHistory = useCallback(() => {
    const params = salesListParamsFromReportsScope(
      new Date(),
      dateRangePreset,
      customRangeParam,
      reportsQueryScope,
      { screenTitle: 'Ventas del período' }
    );
    openSalesList(navigation, params);
  }, [navigation, dateRangePreset, customRangeParam, reportsQueryScope]);

  const openBranchSalesHistoryPending = useCallback(() => {
    const params = salesListParamsFromReportsScope(
      new Date(),
      dateRangePreset,
      customRangeParam,
      reportsQueryScope,
      { paymentPendingOnly: true, screenTitle: 'Ventas con cobro pendiente' }
    );
    openSalesList(navigation, params);
  }, [navigation, dateRangePreset, customRangeParam, reportsQueryScope]);

  const setReportsCustomerFilter = useCallback((id: string, name: string) => {
    dispatchFilters({ type: 'SET_CUSTOMER', id, label: name });
  }, []);

  const clearReportsCustomerFilter = useCallback(() => {
    dispatchFilters({ type: 'CLEAR_CUSTOMER' });
  }, []);

  const setReportsOperatorFilter = useCallback((id: string, name: string) => {
    dispatchFilters({ type: 'SET_OPERATOR', id, label: name });
  }, []);

  const clearReportsOperatorFilter = useCallback(() => {
    dispatchFilters({ type: 'CLEAR_OPERATOR' });
  }, []);

  const pinVentasProductFromCatalog = useCallback((productId: string, label: string) => {
    dispatchFilters({ type: 'PIN_PRODUCT', productId, label: label.trim() ? label.trim() : productId });
  }, []);

  const clearVentasProductPin = useCallback(() => {
    dispatchFilters({ type: 'CLEAR_PIN' });
  }, []);

  const setSalesByProductCategory = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'salesByProductCategory', value: v });
  }, []);
  const setReportsVentasBrandFilter = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'reportsVentasBrandFilter', value: v });
  }, []);
  const setReportsVentasSupplierFilter = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'reportsVentasSupplierFilter', value: v });
  }, []);
  const setReportsCashSessionFilterId = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'reportsCashSessionFilterId', value: v });
  }, []);
  const setReportsHasDiscountFilter = useCallback((v: 'all' | 'yes' | 'no') => {
    dispatchFilters({ type: 'SET', key: 'reportsHasDiscountFilter', value: v });
  }, []);
  const setReportsPromotionFilterId = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'reportsPromotionFilterId', value: v });
  }, []);
  const setReportsPromoCodeFilter = useCallback((v: string | null) => {
    dispatchFilters({ type: 'SET', key: 'reportsPromoCodeFilter', value: v });
  }, []);

  return {
    reportsQueryScope,
    ventasProductFiltersActiveForUi,
    reportsQueryFiltersActive,
    salesByProductCategory,
    setSalesByProductCategory,
    salesByProductCategoryOptions,
    salesByProductItemTypeFilter,
    setSalesByProductItemTypeFilter: handleSalesByProductItemTypeFilterChange,
    reportsVentasPinnedProductId,
    reportsVentasPinnedProductLabel,
    pinVentasProductFromCatalog,
    clearVentasProductPin,
    reportsVentasBrandFilter,
    setReportsVentasBrandFilter,
    reportsVentasSupplierFilter,
    setReportsVentasSupplierFilter,
    reportsVentasBrandOptions,
    reportsVentasSupplierOptions,
    reportsCustomerFilterId,
    reportsCustomerFilterLabel,
    setReportsCustomerFilter,
    clearReportsCustomerFilter,
    reportsOperatorFilterId,
    reportsOperatorFilterLabel,
    setReportsOperatorFilter,
    clearReportsOperatorFilter,
    reportsCashSessionFilterId,
    setReportsCashSessionFilterId,
    reportsHasDiscountFilter,
    setReportsHasDiscountFilter,
    reportsPromotionFilterId,
    setReportsPromotionFilterId,
    reportsPromoCodeFilter,
    setReportsPromoCodeFilter,
    salesByProduct,
    bumpSalesByProductRefreshKey,
    resetVentasFilters,
    openBranchSalesHistory,
    openBranchSalesHistoryPending,
  };
}
